import { initialState } from "./initialState.ts";
import type { AppAction, AppState, WhatsappChat, WhatsappMessage } from "../types.ts";
import { aliasJidsForChat, phoneFromChatJid, sortChatsForList } from "../views/Chat.helpers.ts";

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "PATCH":
      return { ...state, ...action.patch };
    case "HYDRATE_STATE":
      return { ...state, ...action.payload };
    case "SET_AUTH":
      return {
        ...state,
        userEmail: action.payload.email,
        authToken: action.payload.token,
        user: action.payload.user,
        loginError: "",
        loginPending: false,
      };
    case "LOGOUT":
      return {
        ...initialState,
        view: "login",
      };
    case "SET_VIEW":
      return { ...state, view: action.view };
    case "SET_PANEL":
      return { ...state, panel: action.panel };
    case "UPSERT_LEAD": {
      const id = String(action.lead.id);
      const mergedFromId = action.lead.mergedFromId !== undefined && action.lead.mergedFromId !== null
        ? String(action.lead.mergedFromId)
        : "";
      const baseLeads = mergedFromId && mergedFromId !== id
        ? state.leads.filter((lead) => String(lead.id) !== mergedFromId)
        : state.leads;
      const idx = baseLeads.findIndex((lead) => String(lead.id) === id);
      const leads = idx >= 0
        ? baseLeads.map((lead, i) => (i === idx ? action.lead : lead))
        : [...baseLeads, action.lead];
      const chats = mergedFromId && mergedFromId !== id
        ? state.chats.map((chat) => (String(chat.leadId || "") === mergedFromId ? { ...chat, leadId: action.lead.id } : chat))
        : state.chats;
      return {
        ...state,
        leads,
        chats,
        selectedLeadId: state.selectedLeadId === mergedFromId ? id : state.selectedLeadId,
      };
    }
    case "DELETE_LEAD": {
      const id = String(action.id);
      return {
        ...state,
        selectedLeadId: state.selectedLeadId === id ? "" : state.selectedLeadId,
        leads: state.leads.filter((lead) => String(lead.id) !== id),
      };
    }
    case "REPLACE_FUNNELS":
      return {
        ...state,
        funnels: action.funnels,
        activeFunnelId: action.activeFunnelId || state.activeFunnelId,
      };
    case "SET_WHATSAPP_SESSION":
      return { ...state, whatsappSession: action.session };
    case "SET_ACCOUNTS":
      return { ...state, accounts: action.accounts };
    case "UPSERT_ACCOUNT": {
      const idx = state.accounts.findIndex((a) => a.sessionId === action.account.sessionId);
      const accounts = idx >= 0
        ? state.accounts.map((a, i) => (i === idx ? { ...a, ...action.account } : a))
        : [...state.accounts, action.account];
      return { ...state, accounts };
    }
    case "REMOVE_ACCOUNT":
      return {
        ...state,
        accounts: state.accounts.filter((a) => a.sessionId !== action.sessionId),
        accountFilter: state.accountFilter.filter((id) => id !== action.sessionId),
      };
    case "SET_ACCOUNT_FILTER":
      return { ...state, accountFilter: action.sessionIds };
    case "UPSERT_MESSAGE":
      return upsertMessage(state, action.message);
    case "PATCH_CHAT":
      return patchChat(state, action.chat);
    case "SET_CHAT_MESSAGES":
      return {
        ...state,
        chatMessages: {
          ...state.chatMessages,
          [action.chatJid]: action.messages,
        },
      };
    case "SET_CHAT_TAGS":
      return { ...state, chatTags: action.tags };
    default:
      return state;
  }
}

function upsertMessage(state: AppState, message: WhatsappMessage): AppState {
  const chatJid = message.chatJid;
  const list = state.chatMessages[chatJid] ? [...state.chatMessages[chatJid]] : [];
  if (!list.some((item) => item.messageKey === message.messageKey)) {
    list.push(message);
    list.sort((a, b) => (a.tsMs || 0) - (b.tsMs || 0));
  }
  return {
    ...state,
    chatMessages: {
      ...state.chatMessages,
      [chatJid]: list,
    },
    chats: bumpChatPreview(state, message),
  };
}

function patchChat(state: AppState, patch: Partial<WhatsappChat> & { chatJid: string }): AppState {
  const idx = state.chats.findIndex((chat) => chat.chatJid === patch.chatJid);
  if (idx < 0) return state;
  const avatarPatch = avatarFieldsFromPatch(patch);
  const aliasJids = Object.keys(avatarPatch).length
    ? new Set(aliasJidsForChat({ ...state.chats[idx], ...patch }, state.chats))
    : new Set<string>();
  const next = state.chats.map((chat, i) => {
    if (i === idx) return { ...chat, ...patch };
    if (aliasJids.has(chat.chatJid)) return { ...chat, ...avatarPatch };
    return chat;
  });
  return { ...state, chats: patch.pinnedAt !== undefined ? sortChatsForList(next) : next };
}

function avatarFieldsFromPatch(patch: Partial<WhatsappChat>): Partial<WhatsappChat> {
  const avatarPatch: Partial<WhatsappChat> = {};
  if (Object.prototype.hasOwnProperty.call(patch, "avatarUrl")) avatarPatch.avatarUrl = patch.avatarUrl;
  if (Object.prototype.hasOwnProperty.call(patch, "avatarLookupJid")) avatarPatch.avatarLookupJid = patch.avatarLookupJid;
  if (Object.prototype.hasOwnProperty.call(patch, "avatarFetchedAt")) avatarPatch.avatarFetchedAt = patch.avatarFetchedAt;
  return avatarPatch;
}

function bumpChatPreview(state: AppState, message: WhatsappMessage): WhatsappChat[] {
  const idx = state.chats.findIndex((chat) => chat.chatJid === message.chatJid);
  const isGroup = message.chatJid.endsWith("@g.us");
  const isLid = message.chatJid.endsWith("@lid");
  const inferredName = !message.fromMe && message.senderName ? message.senderName : "";
  const inferredPhone = isGroup || isLid ? "" : phoneFromChatJid(message.chatJid);
  const base = idx >= 0
    ? state.chats[idx]
    : { chatJid: message.chatJid, isGroup, pushName: inferredName, phone: inferredPhone };

  const nextChat: WhatsappChat = {
    ...base,
    pushName: idx >= 0 ? inferredName || base.pushName || "" : inferredName,
    lastMsgId: message.messageKey,
    lastText: (message.text || "").slice(0, 500),
    lastKind: message.kind,
    lastFromMe: !!message.fromMe,
    lastStatus: message.status ?? base.lastStatus,
    lastTsMs: message.tsMs || base.lastTsMs || Date.now(),
    unreadCount: message.fromMe || state.activeChatJid === message.chatJid || message.isHistory
      ? base.unreadCount || 0
      : (base.unreadCount || 0) + 1,
    leadId: message.leadId || base.leadId || null,
  };

  return sortChatsForList([nextChat, ...state.chats.filter((chat) => chat.chatJid !== message.chatJid)]);
}
